import { useEffect, useState } from 'react'
import { isRole, roleHasCapability } from '@qim/shared'
import { ApiError, clearBoundary, fetchBoundary, recordBoundary } from '../api.js'
import { Modal } from './modal.jsx'
import { formatInstant } from '../format.js'
import type { BoundaryView } from '../api.js'
import type { BoundaryMark, Capability } from '@qim/shared'
import type { JSX } from 'react'

/**
 * "Permissions boundary", on one instance's screen (IM-192).
 *
 * The customer creates the boundary in their own account; this hands them the
 * policy to create, checks that what they created is that policy, records it,
 * and from then on every run for the instance carries it. Qrvey never creates
 * or edits it.
 *
 * ## What it says without asking anyone
 *
 * Whether a boundary is recorded, from the instance's own record — the API's
 * table, not the customer's account — so the screen says what the next run
 * will carry at no cost.
 *
 * ## What it asks the account, and when
 *
 * The published document and the live policy are two reads into the
 * customer's account, so they are made when somebody opens the check, never on
 * render: the IM-83 rule, and why the check is a dialog that mounts only when
 * open (IM-146).
 *
 * ## Offered by capability, never by role name
 *
 * Recording is `deployment:create`, clearing `account:onboard`. Not a control —
 * the API checks both — but a button that always fails is a worse way to say no
 * than not offering it.
 */

const holds = (role: string, capability: Capability): boolean =>
  isRole(role) && roleHasCapability(role, capability)

/** Saves an artifact as a file, the way the other templates and the bundle do. */
const save = (filename: string, document: unknown): void => {
  const blob = new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = window.document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

const refusalSentence = (error: unknown): string => {
  if (error instanceof ApiError) {
    const body = error.body as { detail?: unknown } | undefined
    if (typeof body?.detail === 'string') return body.detail
    if (error.status === 403) return 'This role cannot do that here.'
  }
  return 'Could not reach the account just now.'
}

export const BoundaryPanel = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
  recorded,
  role,
  onChanged,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
  /** The standing boundary, from the instance's own record. */
  readonly recorded: BoundaryMark | undefined
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element | null => {
  const [open, setOpen] = useState(false)
  if (!holds(role, 'account:read')) return null

  return (
    <section className="panel">
      <h2>Permissions boundary</h2>
      {recorded === undefined ? (
        <p className="muted">
          None recorded. Without one, the IAM roles this instance creates are limited only by what
          the deploy role allows them.
        </p>
      ) : (
        <p>
          Every run sends <code>{recorded.arn}</code> ({recorded.kind}), recorded by {recorded.by}{' '}
          on <time dateTime={recorded.at}>{formatInstant(recorded.at)}</time>.
        </p>
      )}
      <div className="actions">
        <button type="button" className="ghost" onClick={() => setOpen(true)}>
          {recorded === undefined ? 'Set up a boundary' : 'Check the boundary'}
        </button>
      </div>
      {open ? (
        <Modal title="Permissions boundary" onCancel={() => setOpen(false)} wide>
          <BoundaryCheck
            baseUrl={baseUrl}
            tenantId={tenantId}
            accountKey={accountKey}
            deploymentId={deploymentId}
            role={role}
            onChanged={onChanged}
          />
        </Modal>
      ) : null}
    </section>
  )
}

/** The dialog's body: asks the account once when mounted, and again after a change. */
export const BoundaryCheck = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
  role,
  onChanged,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element => {
  const [view, setView] = useState<BoundaryView | undefined>(undefined)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<string | undefined>(undefined)
  const [confirmingClear, setConfirmingClear] = useState(false)
  const [asked, setAsked] = useState(0)

  useEffect(() => {
    let live = true
    setFailure(undefined)
    fetchBoundary(baseUrl, tenantId, accountKey, deploymentId)
      .then((answer) => {
        if (live) setView(answer)
      })
      .catch((error: unknown) => {
        if (live) setFailure(refusalSentence(error))
      })
    return () => {
      live = false
    }
  }, [baseUrl, tenantId, accountKey, deploymentId, asked])

  const act = async (what: 'record' | 'clear'): Promise<void> => {
    setBusy(true)
    setOutcome(undefined)
    try {
      if (what === 'record') {
        await recordBoundary(baseUrl, tenantId, accountKey, deploymentId)
        setOutcome('Recorded. Every run for this instance now carries it.')
      } else {
        const answer = await clearBoundary(baseUrl, tenantId, accountKey, deploymentId)
        setOutcome(answer.note ?? 'Cleared.')
      }
      setConfirmingClear(false)
      onChanged()
      setAsked((n) => n + 1)
    } catch (error: unknown) {
      setOutcome(refusalSentence(error))
    } finally {
      setBusy(false)
    }
  }

  if (failure !== undefined) return <p className="failure">{failure}</p>
  if (view === undefined) return <p className="muted">Asking the account…</p>

  const refused = view.check.state === 'refused' ? view.check : undefined
  const canRecord = holds(role, 'deployment:create')
  const canClear = holds(role, 'account:onboard')
  const recordedMatches = view.recorded !== null && view.check.state === 'match'

  return (
    <div className="boundary-check">
      <p>
        This instance’s boundary is <code>{view.expectedArn}</code>: a policy you create and own,
        which the installer attaches to every IAM role of the instance. Qrvey never creates or edits
        it.
      </p>

      {view.offer === undefined ? null : (
        <>
          <h3>Create it from one of these</h3>
          <p className="muted">
            {view.offer.kind === 'exact'
              ? 'An exact boundary: it names two of this instance’s roles by their exact names, so an upgrade that recreates either one makes it out of date.'
              : 'A pattern boundary: it names this instance’s roles by patterns carrying its id, so an upgrade does not make it out of date.'}
          </p>
          <div className="actions">
            <button
              type="button"
              className="ghost"
              onClick={() => {
                if (view.offer !== undefined)
                  save(view.offer.artifact.filename, view.offer.artifact.document)
              }}
            >
              Download the stack template
            </button>
          </div>
          <p className="muted">Or with the CLI, after saving the document below to that file:</p>
          <pre className="boundary-command">{view.offer.command}</pre>
          <details>
            <summary>The policy document</summary>
            <pre className="boundary-document">{JSON.stringify(view.offer.document, null, 2)}</pre>
          </details>
        </>
      )}

      <h3>What the account holds</h3>
      {view.check.state === 'match' ? (
        <p role="status">
          {recordedMatches
            ? 'Recorded, and the policy in the account still matches.'
            : 'The policy in the account matches what this instance expects.'}
        </p>
      ) : (
        <div role="status">
          <p className={refused?.error === 'boundary-not-published' ? 'muted' : 'failure'}>
            {refused?.detail}
          </p>
          {refused?.differences === undefined ? null : (
            <ul>
              {refused.differences.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
          {/* A recorded boundary that no longer matches still goes out on every
            run — the installer warns, and a stale one can refuse Karpenter its
            PassRole. Said here, where the fix is. */}
          {view.recorded !== null && refused?.error === 'boundary-mismatch' ? (
            <p className="failure">
              The recorded boundary is out of date: update the policy in the account from the
              document above.
            </p>
          ) : null}
        </div>
      )}

      <div className="actions">
        {canRecord && view.check.state === 'match' && !recordedMatches ? (
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => void act('record')}
          >
            Record this boundary
          </button>
        ) : null}
        {canClear && view.recorded !== null && !confirmingClear ? (
          <button
            type="button"
            className="ghost"
            disabled={busy}
            onClick={() => setConfirmingClear(true)}
          >
            Clear the boundary…
          </button>
        ) : null}
      </div>
      {confirmingClear ? (
        <div className="approval-note">
          <p>
            Clearing stops runs carrying this boundary. With an administrator deploy role the next
            apply removes it from every role; with a scoped one, removing a boundary is refused, so
            the next apply fails until one is recorded again.
          </p>
          <div className="actions">
            <button
              type="button"
              className="danger"
              disabled={busy}
              onClick={() => void act('clear')}
            >
              Clear it
            </button>
            <button type="button" className="ghost" onClick={() => setConfirmingClear(false)}>
              Keep it
            </button>
          </div>
        </div>
      ) : null}
      {outcome === undefined ? null : <p role="status">{outcome}</p>}
    </div>
  )
}
